import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { useRecoilValue } from "recoil"

import { isPayingUserSelector, userAtom } from "@/atoms/userAtom"
import { useOrg } from "@/hooks/useOrg"
import { DEFAULT_REASSIGN_TIME_IN_SECONDS, IPreference } from "@/types/organizations.types"
import { MediumText, SecondaryText, SemiBoldLabelText } from "@/UI"
import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Input, Switch } from "antd"
import { AnimatePresence, motion } from "framer-motion"
import { isNil } from "lodash"
import KosmoButton from "@/UI/Buttons/KosmoButton"
import React from "react"

interface IReassignProps {}

const Reassign: React.FC<IReassignProps> = () => {
  const { t } = useTranslation()
  const user = useRecoilValue(userAtom)
  const isPayingUser = useRecoilValue(isPayingUserSelector)
  const { updateOrgPreferences, isLoading } = useOrg()
  const [isReassignEnabled, setIsReassignEnabled] = useState<boolean>(
    user.preference?.platformReassign || false
  )
  const [reassignTime, setReassignTime] = useState<number>()

  const handleToggleReassign = async () => {
    await updateOrgPreferences({
      ...user.preference,
      platformReassign: !isReassignEnabled,
    } as IPreference)
  }

  const handleUpdateReassignTime = async () => {
    await updateOrgPreferences({
      ...user.preference,
      reassignTime: (reassignTime ? reassignTime : DEFAULT_REASSIGN_TIME_IN_SECONDS) * 60,
    } as IPreference)
  }

  useEffect(() => {
    if (!isNil(user.preference?.platformReassign)) {
      setIsReassignEnabled(user.preference?.platformReassign || false)
    }
    setReassignTime(
      user.preference?.reassignTime
        ? user.preference?.reassignTime / 60
        : DEFAULT_REASSIGN_TIME_IN_SECONDS / 60
    )
  }, [user.preference])

  return (
    <>
      <div className="flex w-full">
        <div className="mr-4">
          <Switch
            checked={isReassignEnabled}
            onChange={handleToggleReassign}
            loading={isLoading}
            disabled={!isPayingUser}
          />
        </div>
        <div className="w-full">
          <MediumText className="mb-3">{t("profile.DispatchSettings.enableReassign")}</MediumText>
          <AnimatePresence>
            {isReassignEnabled ? (
              <div className="flex flex-col space-y-3">
                <motion.div
                  className="rounded-md bg-gray-50 px-4 overflow-hidden"
                  initial={{
                    maxHeight: 0,
                    opacity: 0,
                    x: 30,
                    paddingTop: 0,
                    paddingBottom: 0,
                  }}
                  animate={{
                    maxHeight: "150px",
                    opacity: 1,
                    x: 0,
                    paddingTop: 12,
                    paddingBottom: 12,
                  }}
                  exit={{
                    maxHeight: 0,
                    opacity: 0,
                    x: 30,
                    paddingTop: 0,
                    paddingBottom: 0,
                  }}
                >
                  <p className="flex mb-1">
                    <FontAwesomeIcon
                      className="mr-2 text-[#34D399] w-4 h-4 mt-1"
                      icon={faCircleCheck as IconProp}
                    />
                    <span>{t("profile.DispatchSettings.enableReassignOnDesc1")}</span>
                  </p>
                  <p className="flex mb-1">
                    <FontAwesomeIcon
                      className="mr-2 text-[#34D399] w-4 h-4 mt-1"
                      icon={faCircleCheck as IconProp}
                    />
                    <span>{t("profile.DispatchSettings.enableReassignOnDesc2")}</span>
                  </p>
                  <p className="flex mb-1">
                    <FontAwesomeIcon
                      className="mr-2 text-[#34D399] w-4 h-4 mt-1"
                      icon={faCircleCheck as IconProp}
                    />
                    <span>{t("profile.DispatchSettings.enableReassignOnDesc3")}</span>
                  </p>
                  <SecondaryText>
                    {t("profile.DispatchSettings.enableReassignOffDesc")}
                  </SecondaryText>
                </motion.div>
                <div className="flex flex-col">
                  <SemiBoldLabelText>
                    {t("profile.DispatchSettings.reassignTime")}
                  </SemiBoldLabelText>
                  <div className="flex space-x-3">
                    <Input
                      className="withAddOnInput !max-w-[200px]"
                      placeholder={"15"}
                      value={reassignTime}
                      type="number"
                      size="small"
                      onChange={(e) => setReassignTime(Number(e.target.value))}
                      addonAfter="Minutes"
                      style={{ borderRadius: "none !important" }}
                      disabled={!isPayingUser}
                    />
                    {(reassignTime || DEFAULT_REASSIGN_TIME_IN_SECONDS) * 60 !==
                      user?.preference?.reassignTime && (
                      <KosmoButton onClick={handleUpdateReassignTime}>
                        {t("common.save")}
                      </KosmoButton>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <SecondaryText>{t("profile.DispatchSettings.enableReassignOffDesc")}</SecondaryText>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  )
}

export default Reassign
